import {
  BaseCurrency,
  GetDepositPaymentDetailsResponse,
  isAxiosError,
  isDefined,
  Loadable,
} from '@eih-netbroker/common';
import axios from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useAccounts } from '../../../shared/services/useAccounts';
import { getTargetAccountAborterAtom } from '../states/cashActionAtoms';
import {
  depositDetailsAtom,
  externalDepositSelectedCurrencyAtom,
  internalDepositSelectedCurrencyAtom,
  selectedTargetAccountAtom,
  targetAccountsAtom,
} from '../states/depositAtoms';
import { usePayment } from './usePayment';

export const useDeposit = () => {
  const [depositDetails, setDepositDetails] = useAtom(depositDetailsAtom);
  const [selectedExternalCurrency, setSelectedExternalCurrency] = useAtom(externalDepositSelectedCurrencyAtom);
  const setSelectedInternalCurrency = useSetAtom(internalDepositSelectedCurrencyAtom);
  const { getDepositPaymentDetails, getDepositPaymentTargetAccounts, clearPaymentAtomState, handleTwoFactorRestart } =
    usePayment();
  const [, setTargetAccounts] = useAtom(targetAccountsAtom);
  const [, setSelectedTargetAccount] = useAtom(selectedTargetAccountAtom);
  const [getTargetAccountAborter, setGetTargetAccountAborter] = useAtom(getTargetAccountAborterAtom);
  const { getMainClientId } = useAccounts();

  const getTargetAccounts = useCallback(
    async (
      depositDetails: GetDepositPaymentDetailsResponse,
      currencyCode: string,
      abortController: AbortController,
    ) => {
      const sourceAccount = depositDetails.depositPaymentOptions.find(o => o.currencyCode === currencyCode);

      if (!isDefined(sourceAccount)) {
        setTargetAccounts(Loadable.uninitialized);
        setSelectedTargetAccount(undefined);
        return;
      }

      setTargetAccounts(Loadable.loading);
      setSelectedTargetAccount(undefined);

      try {
        const targetAccounts = (
          await getDepositPaymentTargetAccounts(
            {
              clientId: sourceAccount.clientId,
              currencyCode,
              accountNumber: sourceAccount.accountNumber,
            },
            abortController,
          )
        ).data.depositPaymentTargetAccounts;

        setTargetAccounts(Loadable.loaded(targetAccounts));
        setSelectedTargetAccount(targetAccounts[0]);
      } catch (error) {
        if (isAxiosError(error) && axios.isCancel(error)) {
          return;
        }

        setTargetAccounts(Loadable.failed);
        setSelectedTargetAccount(undefined);
      }
    },
    [getDepositPaymentTargetAccounts, setSelectedTargetAccount, setTargetAccounts],
  );

  const refreshDepositDetails = useCallback(async () => {
    getTargetAccountAborter?.abort();

    setDepositDetails(Loadable.loading);
    try {
      const resp = (await getDepositPaymentDetails(getMainClientId())).data;
      setDepositDetails(Loadable.loaded(resp));
      const abortController = new AbortController();
      setGetTargetAccountAborter(abortController);
      getTargetAccounts(resp, BaseCurrency, abortController);
    } catch {
      setDepositDetails(Loadable.failed);
    }
  }, [
    getTargetAccounts,
    getMainClientId,
    setDepositDetails,
    getDepositPaymentDetails,
    setGetTargetAccountAborter,
    getTargetAccountAborter,
  ]);

  const handleModalClose = useCallback(
    (onClose: () => void) => {
      clearPaymentAtomState();
      setSelectedExternalCurrency(BaseCurrency);
      setSelectedInternalCurrency(BaseCurrency);
      onClose();
    },
    [clearPaymentAtomState, setSelectedExternalCurrency, setSelectedInternalCurrency],
  );

  return {
    refreshDepositDetails,
    setSelectedExternalCurrency,
    depositDetails,
    selectedExternalCurrency,
    getTargetAccounts,
    handleModalClose,
    handleTwoFactorRestart,
  };
};
